import { useMemo, useState } from 'react';
import {
    Button,
    ButtonVariant,
    ClinicalHistory,
    ClinicalHistoryCardStyle,
    ClinicalHistoryWrapper,
    Modal,
    Note,
    NotesManager,
    Policy,
    SupportingDocumentDeletionReason,
} from '@barkibu/noma-commons';
import { RequestSummary } from '../../../domain/entities/RequestSummary';
import { UpdateSupportingDocumentDto } from '../../../infraestructure/repositories/incident/dtos/UpdateSupportingDocumentDto';
import PolicyIncidentsBanner from '../../components/organisms/policy-incidents-banner/PolicyIncidentsBanner';
import PolicyIncidentsList from '../../components/organisms/policy-incidents-list/PolicyIncidentsList';
import './policy-incidents-details.modules.scss';

export type PolicyIncidentsDetailsProps = {
    requestSummaries: RequestSummary[];
    onGoBack: () => void;
    onRemoveDocument: (key: string, deletionReason: SupportingDocumentDeletionReason) => Promise<void>;
    onUpdateDocument: (document: UpdateSupportingDocumentDto) => Promise<void>;
    onCloseClaimRequest: (claimRequestKey: string) => void;
    clinicalHistory: ClinicalHistory | undefined;
    policies: Policy[];
    policyNotes: Note[];
    petNotes: Note[];
    petParentNotes: Note[];
    onAddNote: ((note: Omit<Note, 'key' | 'updatedAt'>) => void) | undefined;
    onDeleteNote: (note: Note) => void;
};

const PolicyIncidentsDetails = ({
    requestSummaries,
    onGoBack,
    onRemoveDocument,
    onUpdateDocument,
    onCloseClaimRequest,
    clinicalHistory,
    policies,
    policyNotes,
    petNotes,
    petParentNotes,
    onAddNote,
    onDeleteNote,
}: PolicyIncidentsDetailsProps) => {
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const notes = useMemo(
        () => [...policyNotes, ...petParentNotes, ...petNotes],
        [policyNotes, petParentNotes, petNotes]
    );
    return (
        <div className="policy-incidents__details">
            <section className="policy-incidents__header">
                <PolicyIncidentsBanner onGoBack={onGoBack} />
                {onAddNote && (
                    <>
                        <Button
                            accessibilityText={`Open notes (${notes.length})`}
                            text={`Notes (${notes.length})`}
                            onClick={() => setIsNotesModalOpen(true)}
                            variantName={notes.length > 0 ? ButtonVariant.INFO : ButtonVariant.FILLED_NEUTRAL}
                        />
                        <Modal
                            isOpen={isNotesModalOpen}
                            onClose={() => setIsNotesModalOpen(false)}
                            title="Notes"
                            className="policy-incidents__notes-modal"
                        >
                            <NotesManager notes={notes} onAddNote={onAddNote} onDeleteNote={onDeleteNote} />
                        </Modal>
                    </>
                )}
            </section>

            <ClinicalHistoryWrapper
                clinicalHistory={clinicalHistory}
                policyIdentifier={clinicalHistory?.policyIdentifier || policies[0].identifier} // Clinical History is at the pet level so we SHOULD be able to use any of the policy identifiers
                style={ClinicalHistoryCardStyle.BIG}
            />
            <PolicyIncidentsList
                requestSummaries={requestSummaries}
                policies={policies}
                onRemoveDocument={onRemoveDocument}
                onUpdateDocument={onUpdateDocument}
                onCloseClaimRequest={onCloseClaimRequest}
            />
        </div>
    );
};

export default PolicyIncidentsDetails;
